import ZeissModal from '@components/zeiss-modal';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { Col, Form, Row, TableProps, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import ZeissTable from '@components/table';
import AutoSelectComp from '@components/autoSelect';
import { getCompanyList, getOppIdList } from '@api/project';
import { CompanyResInterface, OppIdListResInterface } from '@api/project/type';
import CopyIcon from '@assets/image/copyContent.svg';
import './CreateProject.scss';

interface Props {
  open: boolean;
  salesUserId: string;
  setOpen: (val: boolean) => void;
  handleOk: (data: OppIdListResInterface) => void;
}

export const SearchOppid = (props: Props) => {
  const { open, salesUserId, setOpen, handleOk } = props;
  const [form] = Form.useForm();
  const [tableData, setTableData] = useState<OppIdListResInterface[]>([]);
  const [copiedItem, setCopiedItem] = useState<OppIdListResInterface | null>(
    null
  );
  const companySelectValue = Form.useWatch('searchOppId', form);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setTableData([]);
      setCopiedItem(null);
    }
  }, [open, form]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (companySelectValue?.length > 0) {
      getOppIdData();
    } else {
      setTableData([]);
    }
  }, [companySelectValue]);

  const handleCopy = async (record: OppIdListResInterface) => {
    await navigator.clipboard.writeText(record.oppoId);
    setCopiedItem(record);

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }

    hideTimerRef.current = setTimeout(() => {
      setCopiedItem(null);
      setTimeout(() => {
        handleOk(record);
      }, 200);
    }, 300);
  };

  const getOppIdData = async () => {
    const res = await getOppIdList({
      salesUserId: salesUserId,
      accountIdList:
        companySelectValue?.map(
          (v: { label: string; value: string }) => v.value
        ) || []
    });
    setTableData(res || []);
  };

  const fetchCompanyOptions = async (
    keyword: string
  ): Promise<CompanyResInterface[]> => {
    if (!keyword?.trim()) {
      return [];
    }

    const res = await getCompanyList({ accountName: keyword });
    return Array.isArray(res) ? res : [];
  };

  const columns: TableProps<OppIdListResInterface>['columns'] = [
    {
      title: 'Account Name',
      dataIndex: 'accountName',
      key: 'accountName',
      width: 120
    },
    {
      title: 'Product',
      dataIndex: 'oppoProductDesc',
      key: 'oppoProductDesc',
      width: 120
    },
    {
      title: 'OppID',
      dataIndex: 'oppoId',
      key: 'oppoId',
      width: 180,
      render: (text: string, record: OppIdListResInterface) => (
        <div>
          <span>{text}</span>
          <Tooltip
            title={<CheckOutlined />}
            color="#87d068"
            arrow={false}
            placement="right"
            open={copiedItem?.oppoId === text}
            rootClassName="copy-tooltip"
          >
            <div className="copy-action" onClick={() => handleCopy(record)}>
              <img className="copy-icon" src={CopyIcon} alt="copy" />
              复制
            </div>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Opp Name',
      dataIndex: 'oppoName',
      key: 'oppoName'
    },
    {
      title: 'Opp Owner',
      dataIndex: 'oppoOwner',
      key: 'oppoOwner',
      width: 100
    },
    {
      title: 'Create Date',
      dataIndex: 'oppoCreatedDate',
      key: 'oppoCreatedDate',
      width: 100
    },
    {
      title: 'Close Date',
      dataIndex: 'oppoCloseDate',
      key: 'oppoCloseDate',
      width: 100
    }
  ];

  return (
    <ZeissModal
      open={open}
      width={1000}
      centered
      footer={null}
      onCancel={() => setOpen(false)}
    >
      <div className="pc-searchOppIdLayout">
        <div className="pc-searchOppIdLayout-title">OppID查询</div>
        <Form form={form} layout="vertical" initialValues={{}}>
          <Row gutter={[24, 0]} justify="space-between">
            <Col span={20}>
              <Form.Item
                label=""
                name="searchOppId"
                rules={[{ required: true, message: '请输入企业名称' }]}
              >
                <AutoSelectComp
                  fetchOptions={fetchCompanyOptions}
                  debounceTimeout={300}
                  placeholder="请输入企业名称搜索"
                  suffixIcon={<SearchOutlined />}
                  mode="multiple"
                  fieldNames={{ label: 'accountName', value: 'accountId' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {companySelectValue?.length ? (
          <>
            <ZeissTable
              rowKey="oppoId"
              className="oppid-table"
              columns={columns}
              dataSource={tableData}
              bordered={false}
              scroll={{ x: 840 }}
              pagination={false}
            />

            {tableData.length ? (
              <div className="tip">点击复制后弹窗自动关闭。</div>
            ) : null}
          </>
        ) : null}
      </div>
    </ZeissModal>
  );
};
